import { should } from "chai";
import { UNIT_OF_MEASURE_OPTIONS } from "Util/constants";
import { PurchaseOrder } from "../../../models/purchase.order";
import { ShipmentContainerInfo } from "../../../models/shipments";
import { ExtraCheckKeys, ExtraCheckValues } from "../../../util/enums";
import {
  Translate,
  addErrorInLine,
  messages as CsvMessages,
  ValidateCSVFieldErrors,
  ValidateCSVError,
} from "../UploadCSVFile/types";
import { CartonTemplateCSV, getCartonTemplateColumnName } from "./util";

export interface ValidateCSVFileExtraCheck {
  label: keyof PurchaseOrder;
  value: number | string;
}

function ExtraCheckTranslate(label: string) {
  switch (label) {
    case ExtraCheckKeys.MODE:
      return ExtraCheckValues.MODE;
    case ExtraCheckKeys.ORIGIN_PORT:
      return ExtraCheckValues.ORIGIN_PORT;
    case ExtraCheckKeys.DESTINATION_PORT:
      return ExtraCheckValues.DISCHARGE_PORT;
    default:
      return "";
  }
}

const getMaximumProductsInCartonMessageError = (
  cartonName: string,
  limit: number,
  translate: Translate
) =>
  `${translate("carton")} ${cartonName} ${translate(
    "alreadyHasTheLimitOfProductsInside"
  )} (${limit})`;
const getDifferentMessageError = (columnName: string, reference: string, translate: Translate) =>
  `${translate("order")} #(${reference}) ${translate(columnName)} ${translate(
    "isDifferentFromBooking"
  )}`;

const orderMissingShipDateMessageError = (
  columnName: string,
  poNumber: string,
  translate: Translate
) => `${columnName} ${poNumber} ${translate("isMissingShipByDate")}`;

const errorQuantityMessage = (
  quantity: number,
  upperLimit: number,
  lowerLimit: number,
  translate: Translate,
  columnName: string,
  value: string
) => {
  if (Math.ceil(quantity * lowerLimit) === Math.floor(quantity * upperLimit)) {
    return `${columnName} (${value}) ${translate("valueShouldBe")} ${Math.ceil(
      quantity * lowerLimit
    )}`;
  }

  return `${columnName} (${value}) ${translate("valueShouldBeBetween")} ${Math.ceil(
    quantity * lowerLimit
  )} ${translate("and")} ${Math.floor(quantity * upperLimit)}`;
};

const VALID_ORIGIN_CRITERION_VALUES = ["W", "P"];

export const messages = (translate: Translate) => {
  return {
    ...CsvMessages(translate),
    maxProductsInCarton: (cartonName: string, limit: number) =>
      getMaximumProductsInCartonMessageError(cartonName, limit, translate),
    wrongRangeQuantity: (
      columnName: string,
      quantity: number,
      upperLimit: number,
      lowerLimit: number,
      value: string
    ) => errorQuantityMessage(quantity, upperLimit, lowerLimit, translate, columnName, value),
    orderMissingShipDate: (columnName: string, poNumber: string) =>
      orderMissingShipDateMessageError(columnName, poNumber, translate),
    isDifferent: (columnName: string, reference: string) =>
      getDifferentMessageError(columnName, reference, translate),
    invalidOriginCriterion: () => translate("originCriterionMustBeWOrP"),
  };
};

export const validateCSVField = (
  dataItem: CartonTemplateCSV,
  line: number,
  allOrders: PurchaseOrder[],
  lineErrors: ValidateCSVFieldErrors,
  translate: Translate,
  isLengthMandatory: boolean,
  extraCheck?: ValidateCSVFileExtraCheck[],
  calledFromShipment: boolean = false,
  containers?: ShipmentContainerInfo[],
  shouldCheckUnitOfMeasure: boolean = false,
  showOriginCriterion: boolean = false
) => {
  const cartonTemplateColumnName = getCartonTemplateColumnName(translate);

  let dataItemValid = true;

  const purchaseOrderFound = allOrders.find((order) => order?.reference === dataItem?.poNumber);

  const productsFromOrderFound = purchaseOrderFound?.purchaseOrderProducts;

  const productFound = productsFromOrderFound?.find(
    (orderProduct) => orderProduct?.product?.sku === dataItem?.sku
  )?.product;

  const addError = (messageError: ValidateCSVError) => {
    addErrorInLine(lineErrors, line, translate(messageError), () => {
      dataItemValid = false;
    });
  };

  const message = messages(translate);

  if (!dataItem.poNumber) addError(message.filled(cartonTemplateColumnName.poNumber));

  if (!dataItem.sku) addError(message.filled(cartonTemplateColumnName.sku));

  if (!dataItem.cartonReference) addError(message.filled(cartonTemplateColumnName.cartonReference));

  if (!dataItem.quantity) addError(message.filled(cartonTemplateColumnName.quantity));

  if (!dataItem.cbm) addError(message.filled(cartonTemplateColumnName.cbm));

  if (!dataItem.weight) addError(message.filled(cartonTemplateColumnName.weight));

  if (!dataItem.hsCode) addError(message.filled(cartonTemplateColumnName.hsCode));

  if (!dataItem.construction) addError(message.filled(cartonTemplateColumnName.construction));

  if (!dataItem.lining) addError(message.filled(cartonTemplateColumnName.lining));

  if (!dataItem.category) addError(message.filled(cartonTemplateColumnName.category));

  if (!dataItem.fabric) addError(message.filled(cartonTemplateColumnName.fabric));

  if (!dataItem.composition) addError(message.filled(cartonTemplateColumnName.composition));

  if (!dataItem.gender) addError(message.filled(cartonTemplateColumnName.gender));

  if (shouldCheckUnitOfMeasure && !dataItem.unitOfMeasure)
    addError(message.filled(cartonTemplateColumnName.unitOfMeasure));

  if (shouldCheckUnitOfMeasure && dataItem.unitOfMeasure) {
    const validValues: string[] = UNIT_OF_MEASURE_OPTIONS(() => {}).map((option) =>
      option.value?.toLowerCase()
    );

    if (!validValues.includes(dataItem.unitOfMeasure?.toLowerCase()))
      addError(message.invalid(cartonTemplateColumnName.unitOfMeasure, dataItem.unitOfMeasure));
  }

  if (!dataItem.manufactureDetails)
    addError(message.filled(cartonTemplateColumnName.manufactureDetails));

  if (isLengthMandatory && !dataItem.length)
    addError(message.filled(cartonTemplateColumnName.length));

  if (showOriginCriterion) {
    if (!dataItem.originCriterion || !VALID_ORIGIN_CRITERION_VALUES.includes(dataItem.originCriterion.toUpperCase())) {
      addError(message.invalidOriginCriterion());
    }
  }

  if (dataItem.poNumber && !purchaseOrderFound)
    addError(message.notfound(cartonTemplateColumnName.poNumber, dataItem.poNumber));

  if (dataItem.sku && !productFound)
    addError(message.notfound(cartonTemplateColumnName.sku, dataItem.sku));

  if (dataItem.quantity) {
    if (Number.isNaN(Number(dataItem.quantity)) || Number(dataItem.quantity) < 1) {
      addError(message.invalid(cartonTemplateColumnName.quantity, dataItem.quantity));
    }
  }

  if (dataItem.weight) {
    if (Number.isNaN(Number(dataItem.weight)) || Number(dataItem.weight) <= 0)
      addError(message.invalid(cartonTemplateColumnName.weight, dataItem.weight));
  }

  if (dataItem.cbm) {
    if (Number.isNaN(Number(dataItem.cbm)) || Number(dataItem.cbm) <= 0)
      addError(message.invalid(cartonTemplateColumnName.cbm, dataItem.cbm));
  }

  // Verify if this column is required based on the context
  // if (calledFromShipment && !dataItem?.container) {
  //   addError(message.filled(cartonTemplateColumnName.container));
  // }

  // if (dataItem?.container) {
  //   const existContainer = containers?.find((item) => item?.container === dataItem?.container);

  //   if (!existContainer) {
  //     addError(message.notfound(cartonTemplateColumnName.container, dataItem.container));
  //   }
  // }

  if (!purchaseOrderFound?.eta)
    addError(message.orderMissingShipDate(cartonTemplateColumnName.poNumber, dataItem?.poNumber));

  if (extraCheck) {
    for (const check of extraCheck) {
      if (check && purchaseOrderFound && purchaseOrderFound[check.label]) {
        if (
          typeof check.value === "string" &&
          typeof purchaseOrderFound[check.label] === "string"
        ) {
          if (
            check.value.toLowerCase() !== purchaseOrderFound[check.label]?.toString().toLowerCase()
          ) {
            addError(
              message.isDifferent(
                ExtraCheckTranslate(`${check.label}`),
                purchaseOrderFound.reference
              )
            );
          }
        } else if (purchaseOrderFound[check.label] !== check.value)
          addError(
            message.isDifferent(ExtraCheckTranslate(`${check.label}`), purchaseOrderFound.reference)
          );
      }
    }
  }

  return dataItemValid;
};

export const validateCSVFields = (
  data: CartonTemplateCSV[],
  allOrders: PurchaseOrder[],
  lineErrors: ValidateCSVFieldErrors,
  translate: Translate,
  isLengthMandatory: boolean = false,
  extraCheck?: ValidateCSVFileExtraCheck[],
  calledFromShipment: boolean = false,
  containers?: ShipmentContainerInfo[],
  shouldCheckUnitOfMeasure: boolean = false,
  showOriginCriterion: boolean = false
) => {
  const valids = new Array(data.length).fill(true, 0);

  data.forEach((dataItem, index) => {
    const line = index + 2;

    valids[index] = validateCSVField(
      dataItem,
      line,
      allOrders,
      lineErrors,
      translate,
      isLengthMandatory,
      extraCheck,
      calledFromShipment,
      containers,
      shouldCheckUnitOfMeasure,
      showOriginCriterion
    );
  });

  return {
    valid: valids.every((valid) => valid),
  };
};
